/**
 * The Bedrock model registry — one entry per distinct model VERSION this repo
 * has ever invoked, never per family. Single source for the USD price catalog
 * (`aiUsdCost.ts`) and, underneath `BEDROCK_MODEL`, for the canonical id
 * string of the four TIERS the codebase actually calls by name today.
 *
 * `BEDROCK_MODEL` is the public surface — every call site imports
 * `BEDROCK_MODEL.HAIKU`/`SONNET`/`OPUS`/`EMBEDDING`, never a literal model
 * id string. Changing which version a tier points at is a one-line edit to
 * `CURRENT_TIER_CATALOG_KEY` below; nothing else in the codebase names a
 * version directly.
 */

import { AiModelFamily } from './aiUsage';

export interface AiModelPriceRates {
  inputPricePerMillionUsd: number;
  outputPricePerMillionUsd: number;
  /** 0 for a model with no published cache-read price (e.g. it doesn't support prompt caching). */
  cacheReadPricePerMillionUsd: number;
  /** 0 for a model with no published cache-write price. Assumes the default (5-minute TTL) cache write rate, matching `DEFAULT_AI_CREDIT_RATES.cacheWriteRate` in `aiCredits.ts`. */
  cacheWritePricePerMillionUsd: number;
}

/**
 * Opus 4.6 and Opus 5 both resolve to `AiModelFamily.OPUS` for credit
 * weighting, but each needs its own row here: a generic `'opus'` substring
 * would silently price a future Opus version at whatever an earlier one
 * cost, which is exactly the guessed-price failure this catalog rules out.
 */
export enum AiBedrockModelKey {
  OPUS_4_6 = 'OPUS_4_6',
  OPUS_5 = 'OPUS_5',
  SONNET_4_6 = 'SONNET_4_6',
  SONNET_5 = 'SONNET_5',
  HAIKU_4_5 = 'HAIKU_4_5',
  TITAN_EMBED_TEXT_V2 = 'TITAN_EMBED_TEXT_V2',
}

export interface AiBedrockModelCatalogEntry {
  key: AiBedrockModelKey;
  /** Substring matched against a lowercased modelId/ARN, mutually exclusive with every other entry's. */
  idMatch: string;
  /** The literal Bedrock inference-profile id — written once, here; `BEDROCK_MODEL` reads it back out. */
  canonicalId: string;
  /** The Global cross-region profile of the same model, for callers that draw on the Global quota pool (Global on-demand rates; the geo profile costs about 10% more). */
  globalCanonicalId?: string;
  family: AiModelFamily;
  prices: AiModelPriceRates;
}

// Source: https://platform.claude.com/docs/en/about-claude/pricing (Anthropic's own
// published rates — base input, 5-minute cache write, cache read, output), cross-checked
// against Bedrock's Global on-demand rate (https://aws.amazon.com/bedrock/pricing/, which
// matches Anthropic's first-party price for every model below). Retrieved 2026-09-21.
// No Amazon Nova entry: grepping non-test apps/**+libs/** today finds no caller — see
// docs/AI_CREDITS_PLAN.md for what to verify before adding one back. OPUS_5/SONNET_5 stay
// even though no tier points at them today: a stack's env override could still name one
// directly, and an unpriced override silently bills at $0 rather than failing loudly.
export const AI_BEDROCK_MODEL_CATALOG: ReadonlyArray<AiBedrockModelCatalogEntry> =
  [
    {
      key: AiBedrockModelKey.OPUS_5,
      idMatch: 'opus-5',
      canonicalId: 'us.anthropic.claude-opus-5',
      family: AiModelFamily.OPUS,
      prices: {
        inputPricePerMillionUsd: 5.0,
        outputPricePerMillionUsd: 25.0,
        cacheReadPricePerMillionUsd: 0.5,
        cacheWritePricePerMillionUsd: 6.25,
      },
    },
    {
      key: AiBedrockModelKey.OPUS_4_6,
      idMatch: 'opus-4-6',
      canonicalId: 'us.anthropic.claude-opus-4-6-v1',
      family: AiModelFamily.OPUS,
      prices: {
        inputPricePerMillionUsd: 5.0,
        outputPricePerMillionUsd: 25.0,
        cacheReadPricePerMillionUsd: 0.5,
        cacheWritePricePerMillionUsd: 6.25,
      },
    },
    {
      key: AiBedrockModelKey.SONNET_5,
      idMatch: 'sonnet-5',
      canonicalId: 'us.anthropic.claude-sonnet-5',
      family: AiModelFamily.SONNET,
      prices: {
        inputPricePerMillionUsd: 2.0,
        outputPricePerMillionUsd: 10.0,
        cacheReadPricePerMillionUsd: 0.2,
        cacheWritePricePerMillionUsd: 2.5,
      },
    },
    {
      key: AiBedrockModelKey.SONNET_4_6,
      idMatch: 'sonnet-4-6',
      canonicalId: 'us.anthropic.claude-sonnet-4-6',
      family: AiModelFamily.SONNET,
      prices: {
        inputPricePerMillionUsd: 3.0,
        outputPricePerMillionUsd: 15.0,
        cacheReadPricePerMillionUsd: 0.3,
        cacheWritePricePerMillionUsd: 3.75,
      },
    },
    {
      key: AiBedrockModelKey.HAIKU_4_5,
      idMatch: 'haiku-4-5',
      canonicalId: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
      globalCanonicalId: 'global.anthropic.claude-haiku-4-5-20251001-v1:0',
      family: AiModelFamily.HAIKU,
      prices: {
        inputPricePerMillionUsd: 1.0,
        outputPricePerMillionUsd: 5.0,
        cacheReadPricePerMillionUsd: 0.1,
        cacheWritePricePerMillionUsd: 1.25,
      },
    },
    {
      key: AiBedrockModelKey.TITAN_EMBED_TEXT_V2,
      idMatch: 'titan-embed-text-v2',
      canonicalId: 'amazon.titan-embed-text-v2:0',
      family: AiModelFamily.TITAN_EMBED,
      prices: {
        // Embeddings charge for input only — there is no generated output to price.
        inputPricePerMillionUsd: 0.02,
        outputPricePerMillionUsd: 0,
        cacheReadPricePerMillionUsd: 0,
        cacheWritePricePerMillionUsd: 0,
      },
    },
  ];

/**
 * Resolves a raw modelId/ARN to its catalog entry — no generic family fallback. First
 * match wins, but every `idMatch` here is a mutually exclusive substring of every other
 * (enforced by aiBedrockModels.spec.ts), so match order never changes the result today.
 */
export const resolveBedrockModelCatalogEntry = (
  modelId: string,
  catalog: ReadonlyArray<AiBedrockModelCatalogEntry> = AI_BEDROCK_MODEL_CATALOG,
): AiBedrockModelCatalogEntry | undefined => {
  const normalizedModelId = modelId.toLowerCase();

  return catalog.find((entry) => normalizedModelId.includes(entry.idMatch));
};

const findCatalogEntryOrThrow = (
  key: AiBedrockModelKey,
): AiBedrockModelCatalogEntry => {
  const entry = AI_BEDROCK_MODEL_CATALOG.find(
    (candidate) => candidate.key === key,
  );

  if (!entry) {
    throw new Error(`aiBedrockModels: no catalog entry for ${key}`);
  }

  return entry;
};

const findGlobalCanonicalIdOrThrow = (key: AiBedrockModelKey): string => {
  const { globalCanonicalId } = findCatalogEntryOrThrow(key);

  if (!globalCanonicalId) {
    throw new Error(`aiBedrockModels: no global profile for ${key}`);
  }

  return globalCanonicalId;
};

/** The single version each tier resolves to today — the only place a tier names a version. */
const CURRENT_TIER_CATALOG_KEY = {
  HAIKU: AiBedrockModelKey.HAIKU_4_5,
  SONNET: AiBedrockModelKey.SONNET_4_6,
  OPUS: AiBedrockModelKey.OPUS_4_6,
  EMBEDDING: AiBedrockModelKey.TITAN_EMBED_TEXT_V2,
} as const;

/**
 * The three Claude tiers plus embedding, addressed by name, never by version — every
 * non-test call site in this codebase imports one of these instead of a literal model id.
 */
export const BEDROCK_MODEL = {
  HAIKU: findCatalogEntryOrThrow(CURRENT_TIER_CATALOG_KEY.HAIKU).canonicalId,
  HAIKU_GLOBAL: findGlobalCanonicalIdOrThrow(CURRENT_TIER_CATALOG_KEY.HAIKU),
  SONNET: findCatalogEntryOrThrow(CURRENT_TIER_CATALOG_KEY.SONNET).canonicalId,
  OPUS: findCatalogEntryOrThrow(CURRENT_TIER_CATALOG_KEY.OPUS).canonicalId,
  EMBEDDING: findCatalogEntryOrThrow(CURRENT_TIER_CATALOG_KEY.EMBEDDING)
    .canonicalId,
} as const;
